import { useState } from "react";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  AdjustStockBody,
  StockAdjustmentReason,
  StockAdjustmentReasons,
  StockAdjustmentResult,
  StockItemDetail,
  StockLocation,
} from "@shared-core/data/api-types";
import { ApiError } from "@shared-core/data/api/errors";
import { formatCount } from "@shared-core/domain/formatters";
import {
  holdingsWithStockAt,
  locationsWithStock,
} from "@shared-core/domain/stock";
import { Button } from "../../app/components/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../app/components/primitives/dialog";
import { Textarea } from "../../app/components/primitives/textarea";
import {
  FieldShell,
  type FieldErrorMap,
  TextField,
  selectClassName,
  toFieldErrorMap,
} from "./StockFormField";

export type AdjustDirection = "in" | "out";

/** Batch select value meaning "start a new batch for this receipt". */
const NEW_BATCH = "__new__";

/** Where a new batch came from: a supplier's PO line, an in-house Tharstern
 * job, or neither (an import or an adjustment). */
type Route = "none" | "supplier" | "in_house";

interface AdjustStockDialogProps {
  item: StockItemDetail;
  direction: AdjustDirection;
  reasons: StockAdjustmentReasons;
  /** Active warehouses, for booking in anywhere. */
  locations: StockLocation[];
  onClose: () => void;
  onAdjusted: (result: StockAdjustmentResult) => void;
}

export function AdjustStockDialog({
  item,
  direction,
  reasons,
  locations,
  onClose,
  onAdjusted,
}: AdjustStockDialogProps) {
  const dataAdapter = useDataAdapter();
  const isIn = direction === "in";
  const options = isIn ? reasons.adjust_in : reasons.adjust_out;
  // Booking out is only offered where the item has stock.
  const locationChoices = isIn
    ? locations.map((l) => ({ location_id: l.id, location_name: l.name }))
    : locationsWithStock(item.holdings.filter((h) => h.location_is_active));

  const [locationId, setLocationId] = useState(
    locationChoices.length === 1 ? locationChoices[0]!.location_id : "",
  );
  const [batchChoice, setBatchChoice] = useState(() =>
    initialBatchChoice(item, direction, locationId),
  );
  const [revision, setRevision] = useState("");
  const [orderLine, setOrderLine] = useState("");
  const [route, setRoute] = useState<Route>("none");
  const [routeRef, setRouteRef] = useState("");
  const [goodsInRef, setGoodsInRef] = useState("");
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState<StockAdjustmentReason | "">("");
  const [note, setNote] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const isNewBatch = batchChoice === NEW_BATCH;
  // Only goods received and an opening balance record where a batch came
  // from; a return or correction that starts a batch has no order line or
  // route, and states its revision when the item is a repeatable one.
  const isReceipt = reason === "goods_received" || reason === "opening_balance";
  const isLinkedItem = item.repeatable_catalogue_item_id != null;
  const batchId = item.tracks_batches && !isNewBatch ? batchChoice : "";
  const heldHere = item.holdings
    .filter(
      (h) =>
        h.location_id === locationId &&
        (!item.tracks_batches || h.batch_id === batchId),
    )
    .reduce((sum, h) => sum + h.on_hand, 0);
  const parsed = Number(quantity);
  const hasQuantity =
    quantity.trim() !== "" && Number.isInteger(parsed) && parsed > 0;
  const delta = hasQuantity ? (isIn ? parsed : -parsed) : null;
  const noteRequired =
    options.find((o) => o.reason === reason)?.requires_note ?? false;
  const batchChosen = !item.tracks_batches || isNewBatch || !!batchId;
  const outBatches = holdingsWithStockAt(item.holdings, locationId);

  function chooseLocation(next: string) {
    setLocationId(next);
    setBatchChoice(initialBatchChoice(item, direction, next));
  }

  async function handleSave() {
    const missing: FieldErrorMap = {};
    if (!locationId) missing.location_id = "Choose a warehouse";
    if (!reason) missing.reason = "Choose a reason";
    if (!batchChosen) missing.batch_id = "Choose which batch";
    if (Object.keys(missing).length) {
      setFieldErrors(missing);
      return;
    }
    setIsSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      const body: AdjustStockBody = {
        location_id: locationId,
        quantity: parsed,
        reason: reason as StockAdjustmentReason,
        note: note.trim() || null,
      };
      if (isIn && goodsInRef.trim()) body.goods_in_ref = goodsInRef.trim();
      if (item.tracks_batches) {
        if (isNewBatch) {
          body.new_batch = {
            artwork_revision_id: revision.trim() || null,
            order_line_id: isReceipt ? orderLine.trim() || null : null,
            purchase_order_line_id:
              isReceipt && route === "supplier"
                ? routeRef.trim() || null
                : null,
            tharstern_job_ref:
              isReceipt && route === "in_house"
                ? routeRef.trim() || null
                : null,
          };
        } else body.batch_id = batchId;
      }
      const result = isIn
        ? await dataAdapter.adjustStockIn(item.id, body)
        : await dataAdapter.adjustStockOut(item.id, body);
      onAdjusted(result);
    } catch (error) {
      console.error("stock.item.adjust_failed", {
        direction,
        status: error instanceof ApiError ? error.status : undefined,
      });
      const map = toFieldErrorMap(error);
      setFieldErrors(map);
      setFormError(
        Object.keys(map).length > 0
          ? "Please fix the highlighted fields and try again."
          : error instanceof ApiError && error.status === 409
            ? "The stock item is archived, or the warehouse is no longer active, so stock can't be moved there."
            : "Couldn't save this adjustment. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isIn ? "Adjust stock in" : "Adjust stock out"}
          </DialogTitle>
          <DialogDescription>
            {item.stock_code} · {item.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
          {formError && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300"
            >
              <AlertTriangle className="size-4 shrink-0 mt-0.5" />
              {formError}
            </div>
          )}

          <FieldShell
            id="adjust-location"
            label="Warehouse"
            error={fieldErrors.location_id}
            hint={
              isIn
                ? undefined
                : "Only warehouses that hold this item are listed."
            }
          >
            {(aria) => (
              <select
                id="adjust-location"
                className={selectClassName}
                value={locationId}
                onChange={(e) => chooseLocation(e.target.value)}
                {...aria}
              >
                <option value="">Choose a warehouse…</option>
                {locationChoices.map((l) => (
                  <option key={l.location_id} value={l.location_id}>
                    {l.location_name ?? l.location_id}
                  </option>
                ))}
              </select>
            )}
          </FieldShell>

          {item.tracks_batches && (
            <FieldShell
              id="adjust-batch"
              label="Batch"
              error={fieldErrors.batch_id ?? fieldErrors.new_batch}
              hint={
                isIn
                  ? "A receipt always starts a new batch. A return or a count correction tops up the batch the stock belongs to, or starts a new batch when you don't know which."
                  : "The oldest batch with stock here is chosen first."
              }
            >
              {(aria) => (
                <select
                  id="adjust-batch"
                  className={selectClassName}
                  value={batchChoice}
                  onChange={(e) => setBatchChoice(e.target.value)}
                  disabled={!locationId}
                  {...aria}
                >
                  <option value="">Choose a batch…</option>
                  {isIn
                    ? item.batches
                        .filter((b) => !b.withdrawn_at)
                        .map((b) => (
                          <option key={b.id} value={b.id}>
                            {batchLabel(b.batch_no, b.artwork_revision_id)}
                          </option>
                        ))
                    : outBatches.map((h) => (
                        <option key={h.id} value={h.batch_id ?? ""}>
                          {batchLabel(h.batch_no, h.artwork_revision_id)} (
                          {formatCount(h.on_hand)} here)
                        </option>
                      ))}
                  {isIn && (
                    <option value={NEW_BATCH}>Start a new batch…</option>
                  )}
                </select>
              )}
            </FieldShell>
          )}

          {item.tracks_batches && isNewBatch && (
            <fieldset className="space-y-3 rounded-lg border border-border p-3">
              <legend className="px-1 text-sm font-medium">
                Where this batch came from
              </legend>
              <TextField
                id="adjust-revision"
                label={
                  !isReceipt && isLinkedItem
                    ? "Artwork revision"
                    : "Artwork revision (optional)"
                }
                value={revision}
                onChange={setRevision}
                error={fieldErrors["new_batch.artwork_revision_id"]}
                placeholder="e.g. Rev 2 - Autumn 2026"
                hint={
                  !isReceipt && isLinkedItem
                    ? "Required: the stock item is linked to a repeatable catalogue item, so every batch records the artwork revision the stock was printed from."
                    : "The artwork revision the stock was printed from."
                }
              />
              {isReceipt && (
                <>
                  <TextField
                    id="adjust-order-line"
                    label="Order line (optional)"
                    value={orderLine}
                    onChange={setOrderLine}
                    error={fieldErrors["new_batch.order_line_id"]}
                    hint="The order line it was delivered against. Leave empty for an import or an adjustment."
                  />
                  <FieldShell id="adjust-route" label="Made by">
                    {(aria) => (
                      <select
                        id="adjust-route"
                        className={selectClassName}
                        value={route}
                        onChange={(e) => setRoute(e.target.value as Route)}
                        {...aria}
                      >
                        <option value="none">Not recorded</option>
                        <option value="supplier">A supplier (PO line)</option>
                        <option value="in_house">
                          In-house (Tharstern job)
                        </option>
                      </select>
                    )}
                  </FieldShell>
                </>
              )}
              {isReceipt && route !== "none" && (
                <TextField
                  id="adjust-route-ref"
                  label={
                    route === "supplier"
                      ? "Purchase order line"
                      : "Tharstern job reference"
                  }
                  value={routeRef}
                  onChange={setRouteRef}
                  error={
                    fieldErrors[
                      route === "supplier"
                        ? "new_batch.purchase_order_line_id"
                        : "new_batch.tharstern_job_ref"
                    ]
                  }
                />
              )}
            </fieldset>
          )}

          <TextField
            id="adjust-quantity"
            label={isIn ? "Quantity to book in" : "Quantity to book out"}
            value={quantity}
            onChange={setQuantity}
            error={fieldErrors.quantity}
            inputMode="numeric"
            hint="Counted in single items."
          />

          <FieldShell
            id="adjust-reason"
            label="Reason"
            error={fieldErrors.reason}
          >
            {(aria) => (
              <select
                id="adjust-reason"
                className={selectClassName}
                value={reason}
                onChange={(e) =>
                  setReason(e.target.value as StockAdjustmentReason)
                }
                {...aria}
              >
                <option value="">Choose a reason…</option>
                {options.map((option) => (
                  <option key={option.reason} value={option.reason}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
          </FieldShell>

          {isIn && (
            <TextField
              id="adjust-goods-in-ref"
              label="Goods-in reference (optional)"
              value={goodsInRef}
              onChange={setGoodsInRef}
              error={fieldErrors.goods_in_ref}
              hint="Each reference can be booked in once for this item."
            />
          )}

          <FieldShell
            id="adjust-note"
            label={noteRequired ? "Note" : "Note (optional)"}
            error={fieldErrors.note}
            hint={
              noteRequired
                ? "Explain the adjustment; this reason needs a note."
                : "e.g. a delivery or job number"
            }
          >
            {(aria) => (
              <Textarea
                id="adjust-note"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                {...aria}
              />
            )}
          </FieldShell>

          <div
            className="rounded-lg border border-border bg-surface px-4 py-3 text-sm space-y-1"
            aria-live="polite"
          >
            {locationId && (
              <BalanceLine
                label={item.tracks_batches ? "Here, this batch" : "Here"}
                before={isNewBatch ? 0 : heldHere}
                delta={delta}
              />
            )}
            <BalanceLine
              label="Total on hand"
              before={item.levels.on_hand}
              delta={delta}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={isSaving || !hasQuantity}
          >
            {isSaving ? "Saving…" : isIn ? "Book in" : "Book out"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Booking out preselects the oldest batch with stock at the warehouse;
 * booking in starts a new batch, the usual case for a receipt. */
function initialBatchChoice(
  item: StockItemDetail,
  direction: AdjustDirection,
  locationId: string,
): string {
  if (!item.tracks_batches) return "";
  if (direction === "in") return NEW_BATCH;
  return holdingsWithStockAt(item.holdings, locationId)[0]?.batch_id ?? "";
}

/** "Batch 2 · Rev 2 - Autumn 2026" for a batch's pick lists. */
export function batchLabel(
  batchNo: number | null | undefined,
  artworkRevisionId: string | null | undefined,
): string {
  return artworkRevisionId
    ? `Batch ${batchNo} · ${artworkRevisionId}`
    : `Batch ${batchNo}`;
}

export function BalanceLine({
  label,
  before,
  delta,
}: {
  label: string;
  before: number;
  delta: number | null;
}) {
  const after = delta == null ? null : before + delta;
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="flex items-center gap-2 font-medium tabular-nums">
        {formatCount(before)}
        <ArrowRight
          className="size-4 text-muted-foreground"
          aria-label="becomes"
        />
        <span
          className={
            after != null && after < 0
              ? "text-red-600 dark:text-red-400"
              : undefined
          }
        >
          {after == null ? "–" : formatCount(after)}
        </span>
      </span>
    </div>
  );
}
